import { defineStore } from 'pinia'
import { ref } from 'vue'

export interface Notification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  duration?: number
}

export const useUiStore = defineStore('ui', () => {
  const sidebarCollapsed = ref(false)
  const notifications = ref<Notification[]>([])
  const loading = ref(false)
  const theme = ref<'light' | 'dark'>('light')

  function toggleSidebar() {
    sidebarCollapsed.value = !sidebarCollapsed.value
    localStorage.setItem('sidebar_collapsed', String(sidebarCollapsed.value))
  }

  function setSidebarCollapsed(collapsed: boolean) {
    sidebarCollapsed.value = collapsed
    localStorage.setItem('sidebar_collapsed', String(collapsed))
  }

  function showNotification(notification: Omit<Notification, 'id'>) {
    const id = `notif-${Date.now()}-${Math.random()}`
    const newNotification: Notification = {
      id,
      duration: 5000,
      ...notification,
    }

    notifications.value.push(newNotification)

    if (newNotification.duration && newNotification.duration > 0) {
      setTimeout(() => {
        removeNotification(id)
      }, newNotification.duration)
    }
  }

  function removeNotification(id: string) {
    const index = notifications.value.findIndex(n => n.id === id)
    if (index > -1) {
      notifications.value.splice(index, 1)
    }
  }

  function clearNotifications() {
    notifications.value = []
  }

  function setLoading(value: boolean) {
    loading.value = value
  }

  function setTheme(newTheme: 'light' | 'dark') {
    theme.value = newTheme
    localStorage.setItem('theme', newTheme)

    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  function initializeUI() {
    const storedSidebarState = localStorage.getItem('sidebar_collapsed')
    if (storedSidebarState) {
      sidebarCollapsed.value = storedSidebarState === 'true'
    }

    const storedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null
    if (storedTheme) {
      setTheme(storedTheme)
    }
  }

  return {
    sidebarCollapsed,
    notifications,
    loading,
    theme,
    toggleSidebar,
    setSidebarCollapsed,
    showNotification,
    removeNotification,
    clearNotifications,
    setLoading,
    setTheme,
    initializeUI,
  }
})
